import { FormHeader } from "../common/forms/FormHeader";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useWatch } from "react-hook-form";
import { SharedContent } from "../api/SharedContent";
import { InputFormField } from "../common/formfields/InputFormField";
import { FormButtons } from "../common/forms/FormButtons";
import { TextareaFormField } from "../common/formfields/TextareaFormField";
import { ToggleFormField } from "../common/formfields/ToggleFormField";
import { DatasetsVirtualizedSelectForm } from "../common/forms/EntityForms/formsVirtualized/DatasetsVirtualizedSelectForm";
import { PasswordFormField } from "../common/formfields/PasswordFormField";
import { EntityFormProps } from "../common/entity/EntityInterfaces";
import styles from "../common/forms/forms.module.css";
import { useGet, useUnpaginate } from "../api/BaseEntityApi";
import { Dataset, datasetsConstants } from "../api/Datasets";
import { useMemo } from "react";
import { Alert } from "../common/overlays/Alert/Alert";

const SharedContentFormSchema = yup.object().shape({
  name: yup.string().required("A name is required"),
  datasets: yup.array().min(1, "At least one dataset is required").required("At least one dataset is required"),
});

export const SharedContentForm = ({
  event,
  fieldLabels,
  permissions,
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
}: EntityFormProps<"sharedContents">) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Partial<SharedContent>>({
    defaultValues: initialValues,
    resolver: yupResolver(SharedContentFormSchema),
  });

  const datasetsValues = useWatch({ control, name: "datasets" });

  const hasDatasets = datasetsValues && Array.isArray(datasetsValues) && datasetsValues.length > 0;
  const { data: datasets } = useUnpaginate<Dataset>(
    datasetsConstants.resource,
    { ids: datasetsValues?.map((d) => d.id) || [] },
    { enabled: hasDatasets }
  );
  const { data: enableMweb } = useGet<boolean>(`mweb/status`, undefined, { enabled: hasDatasets });

  const containsMestreNova = useMemo(
    () => enableMweb && datasets && Object.values(datasets)?.some((d) => d.format?.id === "multi_mestrelab_mnova"),
    [datasets, enableMweb]
  );

  return (
    <>
      <FormHeader title={title} subtitle={subtitle} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">Details</legend>
        </fieldset>

        <InputFormField
          id={"name"}
          label={"Name"}
          errors={errors}
          register={register}
          autoFocus={true}
          required={true}
        />
        <TextareaFormField id="publicNotes" label="Public Notes" errors={errors} register={register} />
        <PasswordFormField
          id={"password"}
          label={"Password"}
          errors={errors}
          register={register}
          required={false}
          showPassword={event === "ADD"}
        />
        <TextareaFormField id="privateNotes" label="Private Notes" errors={errors} register={register} />
        <DatasetsVirtualizedSelectForm id="datasets" control={control} horizontal required isMulti showControls />

        <ToggleFormField id={"allowContentDownload"} label="Allow Download" control={control} horizontal />
        {containsMestreNova && (
          <Alert
            type="info"
            message={
              "Shared content contains MestreNova files. Allowing download will enable viewing in the web interface."
            }
            fit
            centered
          />
        )}
        <FormButtons
          onClose={onCancel}
          onSubmit={handleSubmit(async (entity) => await onSubmit(entity))}
          submitButtonLabel={submitButtonLabel || "Save changes"}
          disabled={isSubmitting}
          errors={errors}
          loading={loading}
          reset={["EDIT", "CLONE"].includes(event) ? () => reset(initialValues) : undefined}
          dirtyFields={["EDIT", "CLONE"].includes(event) ? Object.keys(dirtyFields).length : undefined}
        />
      </form>
    </>
  );
};
